import type {
  Attachment,
  ChatMessage,
  InstitutionCorrespondence,
  CorrespondenceStatus,
  CorrespondenceType
} from '../../../types';

type RawAttachment = Record<string, unknown>;

interface MessageDirectionResult {
  direction: 'inbound' | 'outbound';
  reason: string;
}

/**
 * Determine message direction based on correspondence properties
 *
 * Rules:
 * - response-received status = from institution (inbound)
 * - notification / collection-request / follow-up type = from law firm (outbound)
 * - reply with sent/draft status = from law firm (outbound)
 * - has signatoryUser = from law firm (outbound)
 */
export function determineMessageDirection(
  corr: InstitutionCorrespondence
): MessageDirectionResult {
  // Check status first - most reliable indicator
  if (corr.status === 'response-received') {
    return {
      direction: 'inbound',
      reason: 'response-received status = from institution'
    };
  }

  if (
    corr.type === 'notification' ||
    corr.type === 'collection-request' ||
    corr.type === 'follow-up'
  ) {
    return {
      direction: 'outbound',
      reason: `${corr.type} type = from law firm`
    };
  }

  if (corr.type === 'reply') {
    if (
      corr.status === 'draft' ||
      corr.status === 'sending' ||
      corr.status === 'sent'
    ) {
      return {
        direction: 'outbound',
        reason: `reply with ${corr.status} status = from law firm`
      };
    }
    return {
      direction: 'inbound',
      reason: `reply with ${corr.status} status = from institution`
    };
  }

  if (corr.signatoryUser && corr.signatoryUser.id) {
    return {
      direction: 'outbound',
      reason: 'has signatoryUser = from law firm'
    };
  }

  return {
    direction: 'inbound',
    reason: 'no signatoryUser = from institution'
  };
}

/**
 * Determine sender name based on direction and correspondence data
 */
export function determineSenderName(
  direction: 'inbound' | 'outbound',
  corr: InstitutionCorrespondence,
  institutionName: string
): string {
  if (direction === 'outbound') {
    // From law firm
    if (corr.signatoryUser) {
      return (
        `${corr.signatoryUser.firstName || ''} ${corr.signatoryUser.lastName || ''}`.trim() ||
        'You'
      );
    }
    return 'You';
  }

  // From institution
  return corr.institution?.name || institutionName || 'Institution';
}

/**
 * Convert correspondence attachments to chat message attachments
 */
function convertAttachments(attachments: RawAttachment[]): Attachment[] {
  if (!Array.isArray(attachments)) return [];

  const pick = <T>(att: RawAttachment, key: string): T | undefined =>
    att[key] as T | undefined;

  return attachments
    .map((att): Attachment | null => {
      if (!att || typeof att !== 'object') return null;

      const fileId =
        pick<string | number>(att, 'id') ??
        pick<string | number>(att, 'fileId');
      const fileSizeKb = pick<number>(att, 'fileSizeKb');

      return {
        id: fileId ?? pick<string>(att, 's3Key') ?? Math.random().toString(),
        filename:
          pick<string>(att, 'originalFilename') ??
          pick<string>(att, 'filename') ??
          'Attachment',
        mimeType: pick<string>(att, 'mimeType') ?? 'application/octet-stream',
        size:
          pick<number>(att, 'size') ??
          (fileSizeKb !== undefined ? fileSizeKb * 1024 : undefined),
        url: pick<string>(att, 'url') ?? pick<string>(att, 's3Key'),
        thumbnailUrl:
          pick<string>(att, 'thumbnailUrl') ?? pick<string>(att, 'imageS3Key')
      };
    })
    .filter((att): att is Attachment => att !== null);
}

/**
 * Convert correspondence to chat message
 */
export function convertCorrespondenceToMessage(
  corr: InstitutionCorrespondence,
  institutionName: string
): ChatMessage {
  // Extract email content
  const emailContent =
    corr.fragments?.mainContent || corr.body || corr.content || '';

  const messageTimestamp = new Date(
    corr.sentAt || corr.createdAt || corr.updatedAt || Date.now()
  );

  // Determine direction
  const { direction, reason } = determineMessageDirection(corr);

  // Determine sender
  const senderName = determineSenderName(direction, corr, institutionName);

  console.log(`[MessageTransform] Message ${corr.id} direction logic:`, {
    type: corr.type,
    status: corr.status,
    hasSignatoryUser: !!corr.signatoryUser,
    signatoryUserId: corr.signatoryUser?.id,
    calculatedDirection: direction,
    reason,
    sender: senderName
  });

  return {
    id: String(corr.id),
    content: emailContent,
    sender: senderName,
    timestamp: messageTimestamp,
    direction,
    status: (corr.status || 'sent') as CorrespondenceStatus,
    type: (corr.type || 'notification') as CorrespondenceType,
    attachments: convertAttachments(corr.attachments || []),
    isRead: corr.status !== 'received',
    isDraft: false,
    subject: corr.subject // Include actual subject from correspondence
  };
}

/**
 * Filter messages - only include sent/received, exclude drafts
 */
export function filterMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages.filter((msg) => {
    const normalizedStatus = (msg.status as string).toLowerCase();

    // Include all sent, sending, and received messages
    if (
      normalizedStatus === 'sent' ||
      normalizedStatus === 'sending' ||
      normalizedStatus === 'received' ||
      normalizedStatus === 'response-received'
    ) {
      return true;
    }

    // Exclude ALL drafts (they're handled in the textarea)
    if (normalizedStatus === 'draft') {
      console.log(
        `[MessageTransform] Filtered out draft message ${msg.id}: status="${msg.status}" type="${msg.type}"`
      );
      return false;
    }

    console.log(
      `[MessageTransform] Filtered out message ${msg.id}: status="${msg.status}" type="${msg.type}"`
    );
    return false;
  });
}

/**
 * Sort messages chronologically
 */
export function sortMessagesChronologically(
  messages: ChatMessage[]
): ChatMessage[] {
  return messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}
